import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Alert,
  CircularProgress,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Label as LabelIcon,
  VerifiedUser as ApproveIcon,
  Pending as PendingIcon,
  Block as BlockIcon
} from '@mui/icons-material';
import CostSummaryCard from './CostSummaryCard';
import PerVialCalculator from './PerVialCalculator';

const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' 
  ? 'https://barebiologics-api-bdgjaefwc4h7d9gu.eastus2-01.azurewebsites.net/api'
  : 'http://localhost:8080/api');

const formatDate = (date) => {
  if (!date) return '-';
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return '-';
  return parsed.toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const ProductReleaseTab = ({ donor, onRefresh }) => {
  const [approving, setApproving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [updatingStage, setUpdatingStage] = useState(null);
  
  // Three-stage approval states
  const [stage1Status, setStage1Status] = useState(donor?.approvalStage1Status || 'PENDING');
  const [stage2Status, setStage2Status] = useState(donor?.approvalStage2Status || 'PENDING');
  const [stage3Status, setStage3Status] = useState(donor?.approvalStage3Status || 'PENDING');
  const [stage1Notes, setStage1Notes] = useState(donor?.approvalStage1Notes || '');
  const [stage2Notes, setStage2Notes] = useState(donor?.approvalStage2Notes || '');
  const [stage3Notes, setStage3Notes] = useState(donor?.approvalStage3Notes || '');

  // Check prerequisites
  const eligibilityDocs = donor?.eligibilityDocuments || [];
  const uploadedDocs = eligibilityDocs.filter(d => d.status === 'UPLOADED' || d.status === 'APPROVED');
  const totalRequiredDocs = eligibilityDocs.length || 10; // Use actual count from backend, default to 10
  const eligibilityComplete = uploadedDocs.length >= totalRequiredDocs;
  
  const rawMaterialsFinalized = donor?.rawMaterialsFinalized === true;
  const manufacturingComplete = (donor?.manufacturedVials || []).length > 0;
  const qcTestingComplete = donor?.qcTestingComplete === true;
  const productReleaseApproved = donor?.productReleaseApproved === true;
  
  const allPrerequisitesMet = eligibilityComplete && rawMaterialsFinalized && 
                               manufacturingComplete && qcTestingComplete;
  const canFinalLabel = allPrerequisitesMet && productReleaseApproved;
  const isReleased = donor?.status === 'RELEASED';
  
  // Check if all three approval stages are approved
  const allStagesApproved = stage1Status === 'APPROVED' && 
                            stage2Status === 'APPROVED' && 
                            stage3Status === 'APPROVED';
  
  // Check if can approve each stage (previous stages must be approved)
  const canApproveStage1 = allPrerequisitesMet;
  const canApproveStage2 = allPrerequisitesMet && stage1Status === 'APPROVED';
  const canApproveStage3 = allPrerequisitesMet && stage1Status === 'APPROVED' && stage2Status === 'APPROVED';

  const handleStageStatusChange = async (stage, newStatus, notes) => {
    setUpdatingStage(stage);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/donors/${donor.id}/approval-stage/${stage}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus, notes: notes || null })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || `Failed to update approval stage ${stage}`);
      }

      // Update local state
      if (stage === 1) {
        setStage1Status(newStatus);
        setStage1Notes(notes || '');
      } else if (stage === 2) {
        setStage2Status(newStatus);
        setStage2Notes(notes || '');
      } else if (stage === 3) {
        setStage3Status(newStatus);
        setStage3Notes(notes || '');
      }

      setSuccess(`Approval Stage ${stage} updated to ${newStatus}`);
      if (onRefresh) await onRefresh();
    } catch (err) {
      console.error(`Error updating approval stage ${stage}:`, err);
      setError(err.message || `Failed to update approval stage ${stage}`);
    } finally {
      setUpdatingStage(null);
    }
  };

  const handleApproveRelease = async () => {
    setApproving(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${API_BASE_URL}/donors/${donor.id}/product-release-enhanced`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ notes: null })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to approve product release');
      }

      const releasedVials = data.data?.releasedVials || 0;
      const inventoryBatches = data.data?.inventoryBatches || 0;
      setSuccess(`Product release approved! ${releasedVials} vials released in ${inventoryBatches} batch(es). Check the Receive Inventory Completed tab.`);
      if (onRefresh) await onRefresh();
    } catch (err) {
      console.error('Error approving product release:', err);
      setError(err.message || 'Failed to approve product release');
    } finally {
      setApproving(false);
    }
  };


  const ChecklistItem = ({ label, complete, detail }) => (
    <ListItem sx={{ py: 1 }}>
      <ListItemIcon>
        {complete ? (
          <CheckCircleIcon color="success" />
        ) : (
          <CancelIcon color="error" />
        )}
      </ListItemIcon>
      <ListItemText 
        primary={label}
        secondary={detail || (complete ? 'Complete' : 'Pending')}
        primaryTypographyProps={{ fontWeight: complete ? 'normal' : 'bold' }}
      />
    </ListItem>
  );

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Released Status */}
      {isReleased && (
        <Alert severity="success" sx={{ mb: 3 }} icon={<CheckCircleIcon />}>
          <Typography variant="body1" fontWeight="bold">
            Donor Released to Inventory
          </Typography>
          <Typography variant="body2">
            Released by {donor.releasedByName} on {formatDate(donor.releasedAt)}
          </Typography>
        </Alert>
      )}

      <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
        Product Release Approval
      </Typography>

      {/* Prerequisites Checklist */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
            Prerequisites Checklist
          </Typography>
          <List dense disablePadding>
            <ChecklistItem 
              label={`Recovery Documentation (${uploadedDocs.length}/${totalRequiredDocs})`}
              complete={eligibilityComplete}
              detail={eligibilityComplete ? `All ${totalRequiredDocs} documents uploaded` : `${totalRequiredDocs - uploadedDocs.length} documents remaining`}
            />
            <ChecklistItem 
              label="Raw Materials Finalized"
              complete={rawMaterialsFinalized}
              detail={rawMaterialsFinalized && donor.totalRawMaterialCost 
                ? `Total cost: ${parseFloat(donor.totalRawMaterialCost).toFixed(2)}` 
                : null}
            />
            <ChecklistItem 
              label="Manufacturing Complete"
              complete={manufacturingComplete}
              detail={manufacturingComplete 
                ? `${(donor?.manufacturedVials || []).reduce((sum, v) => sum + (v.quantity || 0), 0)} vials manufactured`
                : null}
            />
            <ChecklistItem 
              label="QC Testing Complete"
              complete={qcTestingComplete}
              detail={qcTestingComplete 
                ? `Completed ${formatDate(donor.qcTestingCompletedAt)}`
                : null}
            />
          </List>
        </CardContent>
      </Card>

      {/* Enhanced Cost Summary */}
      <CostSummaryCard donor={donor} />
      
      {/* Per Vial Calculator */}
      <PerVialCalculator donor={donor} />

      {/* Three-Stage Approval Checklist */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 3 }}>
            Three-Stage Approval Checklist
          </Typography>
          
          {/* Stage 1 */}
          <Box sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" fontWeight="600">
                Stage 1: Quality Review
              </Typography>
              <Chip 
                label={stage1Status}
                color={stage1Status === 'APPROVED' ? 'success' : stage1Status === 'DECLINED' ? 'error' : 'default'}
                icon={stage1Status === 'APPROVED' ? <CheckCircleIcon /> : stage1Status === 'DECLINED' ? <BlockIcon /> : <PendingIcon />}
              />
            </Box>
            {stage1Status === 'APPROVED' && donor?.approvalStage1ApprovedByName && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Approved by {donor.approvalStage1ApprovedByName} on {formatDate(donor.approvalStage1ApprovedAt)}
              </Typography>
            )}
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={stage1Status}
                onChange={(e) => handleStageStatusChange(1, e.target.value, stage1Notes)}
                disabled={!canApproveStage1 || updatingStage === 1}
                label="Status"
              >
                <MenuItem value="PENDING">Pending</MenuItem>
                <MenuItem value="APPROVED">Approved</MenuItem>
                <MenuItem value="DECLINED">Declined</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Notes (optional)"
              value={stage1Notes}
              onChange={(e) => setStage1Notes(e.target.value)}
              onBlur={() => handleStageStatusChange(1, stage1Status, stage1Notes)}
              disabled={updatingStage === 1}
              placeholder="Add notes for this approval stage..."
            />
          </Box>

          {/* Stage 2 */}
          <Box sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" fontWeight="600">
                Stage 2: Compliance Review
              </Typography>
              <Chip 
                label={stage2Status}
                color={stage2Status === 'APPROVED' ? 'success' : stage2Status === 'DECLINED' ? 'error' : 'default'}
                icon={stage2Status === 'APPROVED' ? <CheckCircleIcon /> : stage2Status === 'DECLINED' ? <BlockIcon /> : <PendingIcon />}
              />
            </Box>
            {stage2Status === 'APPROVED' && donor?.approvalStage2ApprovedByName && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Approved by {donor.approvalStage2ApprovedByName} on {formatDate(donor.approvalStage2ApprovedAt)}
              </Typography>
            )}
            {!canApproveStage2 && stage1Status !== 'APPROVED' && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Stage 1 must be approved before Stage 2 can be approved.
              </Alert>
            )}
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={stage2Status}
                onChange={(e) => handleStageStatusChange(2, e.target.value, stage2Notes)}
                disabled={!canApproveStage2 || updatingStage === 2}
                label="Status"
              >
                <MenuItem value="PENDING">Pending</MenuItem>
                <MenuItem value="APPROVED">Approved</MenuItem>
                <MenuItem value="DECLINED">Declined</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Notes (optional)"
              value={stage2Notes}
              onChange={(e) => setStage2Notes(e.target.value)}
              onBlur={() => handleStageStatusChange(2, stage2Status, stage2Notes)}
              disabled={updatingStage === 2}
              placeholder="Add notes for this approval stage..."
            />
          </Box>

          {/* Stage 3 */}
          <Box sx={{ mb: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" fontWeight="600">
                Stage 3: Final Approval
              </Typography>
              <Chip 
                label={stage3Status}
                color={stage3Status === 'APPROVED' ? 'success' : stage3Status === 'DECLINED' ? 'error' : 'default'}
                icon={stage3Status === 'APPROVED' ? <CheckCircleIcon /> : stage3Status === 'DECLINED' ? <BlockIcon /> : <PendingIcon />}
              />
            </Box>
            {stage3Status === 'APPROVED' && donor?.approvalStage3ApprovedByName && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Approved by {donor.approvalStage3ApprovedByName} on {formatDate(donor.approvalStage3ApprovedAt)}
              </Typography>
            )}
            {!canApproveStage3 && (stage1Status !== 'APPROVED' || stage2Status !== 'APPROVED') && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Stages 1 and 2 must be approved before Stage 3 can be approved.
              </Alert>
            )}
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={stage3Status}
                onChange={(e) => handleStageStatusChange(3, e.target.value, stage3Notes)}
                disabled={!canApproveStage3 || updatingStage === 3}
                label="Status"
              >
                <MenuItem value="PENDING">Pending</MenuItem>
                <MenuItem value="APPROVED">Approved</MenuItem>
                <MenuItem value="DECLINED">Declined</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              multiline
              rows={2}
              label="Notes (optional)"
              value={stage3Notes}
              onChange={(e) => setStage3Notes(e.target.value)}
              onBlur={() => handleStageStatusChange(3, stage3Status, stage3Notes)}
              disabled={updatingStage === 3}
              placeholder="Add notes for this approval stage..."
            />
          </Box>
        </CardContent>
      </Card>

      {/* Approve Button */}
      {!isReleased && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          {productReleaseApproved ? (
            <Alert severity="success" icon={<ApproveIcon />} sx={{ mb: 2 }}>
              <Typography variant="body1" fontWeight="bold">
                Product Release Approved
              </Typography>
              <Typography variant="body2">
                Approved by {donor.productReleaseApprovedByName} on {formatDate(donor.productReleaseApprovedAt)}
              </Typography>
              {donor.productReleaseNotes && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Notes: {donor.productReleaseNotes}
                </Typography>
              )}
            </Alert>
          ) : (
            <>
              {!allPrerequisitesMet && (
                <Alert severity="warning" sx={{ mb: 3 }}>
                  Complete all prerequisites before approving product release.
                </Alert>
              )}
              
              {!allStagesApproved && (
                <Alert severity="warning" sx={{ mb: 3 }}>
                  All three approval stages must be approved before final product release.
                </Alert>
              )}
              
              <Button
                variant="contained"
                color="primary"
                size="large"
                startIcon={approving ? <CircularProgress size={24} color="inherit" /> : <ApproveIcon />}
                onClick={handleApproveRelease}
                disabled={!allPrerequisitesMet || !allStagesApproved || approving}
                sx={{ 
                  px: 6, 
                  py: 2,
                  fontSize: '1.2rem',
                  fontWeight: 'bold',
                  boxShadow: (allPrerequisitesMet && allStagesApproved) ? 4 : 0
                }}
              >
                {approving ? 'Approving and Creating Inventory...' : 'Approve Product Release'}
              </Button>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                This will approve the product release and create inventory batches in the completed tab.
                All three approval stages must be completed first.
              </Typography>
            </>
          )}
        </Box>
      )}

      {/* Already Released Message */}
      {isReleased && (
        <Box sx={{ textAlign: 'center', py: 3 }}>
          <Alert severity="success" icon={<CheckCircleIcon />}>
            <Typography variant="body1" fontWeight="bold">
              Donor Released to Inventory
            </Typography>
            <Typography variant="body2">
              Released by {donor.releasedByName} on {formatDate(donor.releasedAt)}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              View the Receive Inventory Completed tab to see the created batches.
            </Typography>
          </Alert>
        </Box>
      )}
    </Box>
  );
};

export default ProductReleaseTab;